import { Link } from 'react-router-dom';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import { Label } from '@givebutter/primitives/label';
import * as RadioGroup from '@givebutter/primitives/radio-group';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { Switch } from '@givebutter/primitives/switch';
import { unmaskNumber } from '@givebutter/util';
import * as Form from 'components/form';
import * as AuctionTooltip from 'components/pages/campaign-settings/campaign-auction/AuctionTooltip';
import * as BidderNumberConfirmation from 'components/pages/campaign-settings/campaign-auction/auction-settings/BidderNumberConfirmation';
import { CancelAuction } from 'components/pages/campaign-settings/campaign-auction/auction-settings/CancelAuction';
import { DeleteAuction } from 'components/pages/campaign-settings/campaign-auction/auction-settings/DeleteAuction';
import { useAuctionSettings } from 'components/pages/campaign-settings/campaign-auction/auction-settings/useAuctionSettings';
import { IntercomProductTourTargets } from 'constants/intercom';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import { getMinDate } from 'helpers/dates';
import { useFlags } from 'launchdarkly-react-client-sdk';

const AuctionSettings = () => {
    const { accountId } = useCurrentAccount();
    const { data: campaign } = useCurrentCampaign();
    const {
        auction,
        onSubmit,
        termsRequired,
        setTermsRequired,
        showDeleteAuction,
        setShowDeleteAuction,
        onDelete,
        onDeleteConfirm,
        showCancelAuction,
        setShowCancelAuction,
        showConfirmationDialog,
        onSubmitConfirmation,
        onCancel,
        onCancelConfirm,
        methods,
        isFetching,
    } = useAuctionSettings();
    const { control, setFocus, handleSubmit, path, watch } = methods;
    const [allowOnlineBidding] = watch(['allowOnlineBidding']);
    const { cancelAuction = false, inPersonAuctionItems = false } = useFlags();

    return (
        <Form.Provider {...methods}>
            <Box>
                <form className={'contents'} onSubmit={handleSubmit(onSubmit)}>
                    <div className={'flex flex-col space-y-6 p-8'}>
                        <div className={'flex flex-col gap-y-1'}>
                            <div className={'flex items-center justify-between'}>
                                <h2
                                    data-intercom-target={
                                        IntercomProductTourTargets.AuctionSettingsTitle
                                    }
                                    className={'text-2xl font-medium'}
                                >
                                    Date &amp; time
                                </h2>
                                {cancelAuction &&
                                    !!auction &&
                                    !auction.can_delete &&
                                    !auction.can_cancel && (
                                        <p
                                            className={
                                                'rounded-full bg-red-100 px-2 font-medium text-red-500'
                                            }
                                        >
                                            Cancelled
                                        </p>
                                    )}
                            </div>
                            <div className={'flex items-center gap-x-2'}>
                                <p className={'text-base text-gray-600'}>
                                    Choose the start and end time for your auction. Change your time
                                    zone{' '}
                                    <Link
                                        to={`/accounts/${accountId}/campaigns/${campaign.id}/settings`}
                                    >
                                        here
                                    </Link>
                                    .
                                </p>
                                <AuctionTooltip.Root>
                                    <AuctionTooltip.Trigger />
                                    <AuctionTooltip.Content>
                                        Auctions are visible when a campaign is published, but all
                                        items are unable to be bid or purchased until bidding opens.
                                    </AuctionTooltip.Content>
                                </AuctionTooltip.Root>
                            </div>
                        </div>
                        <Form.UseFormDateTimePicker
                            name={'date'}
                            control={control}
                            rules={{ required: 'Must set a bidding range for your auction.' }}
                            minDate={getMinDate()}
                        />
                        <Form.Error name={path('date')} />
                        <Form.UseFormContainer className={'flex flex-row justify-between gap-y-1'}>
                            <div className={'flex flex-col gap-y-1'}>
                                <Form.UseFormLabel name="isPublished">
                                    Publish auction
                                </Form.UseFormLabel>
                                <p className={'text-sm text-gray-600'}>
                                    When enabled, your auction will be publicly viewable from your
                                    campaign. When disabled your auction will not be displayed on
                                    your campaign.
                                </p>
                            </div>
                            <Form.Switch
                                name={'isPublished'}
                                control={control}
                                disabled={auction ? !auction.can_delete : false}
                            />
                        </Form.UseFormContainer>
                    </div>
                    <div className={'flex flex-col space-y-6 p-8'}>
                        <div className={'flex flex-col gap-y-1'}>
                            <h2 className={'text-2xl font-medium'}>Bidding &amp; payments</h2>
                            <p className={'text-base text-gray-600'}>
                                Select the defaults for bidding and collecting payment from winners.
                            </p>
                        </div>
                        {inPersonAuctionItems && (
                            <Form.UseFormContainer
                                className={'flex flex-row justify-between gap-y-1'}
                            >
                                <div className={'flex flex-col gap-y-1'}>
                                    <Form.UseFormLabel name={path('allowOnlineBidding')}>
                                        Allow online bidding
                                    </Form.UseFormLabel>
                                    <p className={'text-sm text-gray-600'}>
                                        When enabled, bidders can place bids online unless an item
                                        is specifically marked as in-person only.
                                    </p>
                                    <p className={'text-sm text-gray-600'}>
                                        When disabled, this auction becomes view-only and all online
                                        bidding is not allowed regardless of individual item
                                        settings.
                                    </p>
                                </div>
                                <Form.Switch name={'allowOnlineBidding'} control={control} />
                            </Form.UseFormContainer>
                        )}
                        {allowOnlineBidding && (
                            <div className="flex gap-6">
                                {allowOnlineBidding && (
                                    <div className="flex flex-1 flex-col gap-2">
                                        <Label
                                            data-intercom-target={
                                                IntercomProductTourTargets.AuctionSettingsBiddingMethod
                                            }
                                        >
                                            Bidding method
                                        </Label>
                                        <Form.SingleSelect control={control} name="biddingMethod">
                                            <SingleSelect.Trigger aria-label=" Bidding method" />
                                            <SingleSelect.Content>
                                                <SingleSelect.Item
                                                    value="manual"
                                                    className="max-w-[--radix-select-trigger-width] flex-col items-start gap-1"
                                                >
                                                    <p className="font-medium">
                                                        <SingleSelect.ItemText>
                                                            Standard bidding (Default)
                                                        </SingleSelect.ItemText>
                                                    </p>
                                                    <p className="font-normal">
                                                        Bidders place one bid at a time. If a bidder
                                                        is outbid, they receive a notification to
                                                        place another bid.
                                                    </p>
                                                </SingleSelect.Item>
                                                <SingleSelect.Item
                                                    value="auto"
                                                    className="max-w-[--radix-select-trigger-width] flex-col items-start gap-1"
                                                >
                                                    <p className="font-medium">
                                                        <SingleSelect.ItemText>
                                                            Automated bidding
                                                        </SingleSelect.ItemText>
                                                    </p>
                                                    <p className="font-normal">
                                                        Bidders set a maximum bid amount. If a
                                                        bidder is outbid, an automatic bid will be
                                                        placed up to their max bid amount.
                                                    </p>
                                                </SingleSelect.Item>
                                            </SingleSelect.Content>
                                        </Form.SingleSelect>
                                    </div>
                                )}
                                {allowOnlineBidding && (
                                    <div className="flex flex-1 flex-col justify-between gap-2">
                                        <Form.Label name={path('startingBidderNumber')}>
                                            Starting bidder number
                                        </Form.Label>
                                        <Form.TextInputRoot
                                            control={control}
                                            name={'startingBidderNumber'}
                                            rules={{
                                                required: 'Starting bidder number is required.',
                                                validate: {
                                                    greaterThanZero: value =>
                                                        unmaskNumber(value) >= 1 ||
                                                        'The starting bidder number must be at least 1.',
                                                },
                                            }}
                                            inputRootProps={{
                                                mask: 'whole-number',
                                            }}
                                        >
                                            <Form.TextInputForm placeholder={'1'} />
                                        </Form.TextInputRoot>
                                        <Form.Error name={path('startingBidderNumber')} />
                                    </div>
                                )}
                            </div>
                        )}
                        <div className="flex flex-col gap-2">
                            <Label>Invoicing</Label>
                            <Form.UseFormRadioGroup
                                orientation={'horizontal'}
                                name={'invoiceMethod'}
                                control={control}
                                className={'flex-col sm:flex-col'}
                                variant={'card'}
                            >
                                <RadioGroup.Item
                                    value={'item_close'}
                                    className="flex gap-x-3"
                                    size={'sm'}
                                >
                                    <RadioGroup.IndicatorContainer>
                                        <RadioGroup.Indicator />
                                    </RadioGroup.IndicatorContainer>
                                    <div className={'flex flex-1 flex-col'}>
                                        <Form.Label
                                            name={path('invoiceMethod')}
                                            htmlFor={'item_close'}
                                            className={'font-medium'}
                                        >
                                            Immediately request payment when an item is won
                                        </Form.Label>
                                        <p className={'text-sm text-gray-600'}>
                                            Send a payment request to the winning bidder after an
                                            item has been won or purchased.
                                        </p>
                                    </div>
                                </RadioGroup.Item>
                                <RadioGroup.Item
                                    value={'auction_close'}
                                    className="flex gap-x-3"
                                    size={'sm'}
                                >
                                    <RadioGroup.IndicatorContainer>
                                        <RadioGroup.Indicator />
                                    </RadioGroup.IndicatorContainer>
                                    <div className={'flex flex-1 flex-col'}>
                                        <Form.Label
                                            name={path('invoiceMethod')}
                                            htmlFor={'auction_close'}
                                            className={'font-medium'}
                                        >
                                            Request payment when auction closes
                                        </Form.Label>
                                        <p className={'text-sm text-gray-600'}>
                                            One payment request is sent collectively at auction
                                            close for any item(s) won.
                                        </p>
                                    </div>
                                </RadioGroup.Item>
                            </Form.UseFormRadioGroup>
                        </div>

                        <div className={'flex justify-between gap-y-1'}>
                            <div className={'flex flex-col gap-y-2'}>
                                <Form.Label
                                    name={path('autoCharge')}
                                    className={'text-base font-medium'}
                                >
                                    Auto-Charge Payment Method
                                </Form.Label>
                                <p className={'text-sm text-gray-600'}>
                                    Automatically charge bidders when they win an item. Auto-charge
                                    transactions will incur a 5% platform fee in addition to the
                                    2.9% + $0.30 processing fee.
                                </p>
                                <p className={'text-sm text-gray-600'}>
                                    Bidders who have elected to cover fees when registering for your
                                    auction will be charged for the item plus applicable fees. If a
                                    bidder does not elect to cover fees, they will only be charged
                                    for the item. Auto-charged winners cannot be required to cover
                                    fees.
                                </p>
                            </div>
                            <Form.Switch name={path('autoCharge')} control={control} />
                        </div>
                    </div>
                    <div className={'flex flex-col gap-y-4 p-8'}>
                        <div className={'flex flex-col gap-y-1'}>
                            <h2 className={'text-2xl font-medium'}>Registration</h2>
                            <p className={'text-base text-gray-600'}>
                                Choose the information required during bidder registration.
                            </p>
                        </div>
                        <div className={'flex items-center justify-between'}>
                            <div className={'flex items-center gap-x-4'}>
                                <Form.Label
                                    name={path('requirePaymentMethod')}
                                    className={'font-medium'}
                                >
                                    Require payment information &amp; shipping address
                                </Form.Label>
                                <p
                                    className={
                                        'hidden rounded-full bg-blue-50 pb-1 pl-2 pr-2 pt-1 text-xs font-medium text-blue-500 md:block'
                                    }
                                >
                                    Recommended
                                </p>
                            </div>
                            <Form.Switch name={path('requirePaymentMethod')} control={control} />
                        </div>
                        <div className={'flex items-center justify-between'}>
                            <Form.Label
                                name={path('allowAnonymousBidding')}
                                className={'font-medium'}
                            >
                                Allow anonymous bidding
                            </Form.Label>
                            <Form.Switch name={path('allowAnonymousBidding')} control={control} />
                        </div>
                        <div className={'flex items-center justify-between'}>
                            <Label htmlFor={'terms'}>Custom terms &amp; conditions</Label>
                            <Switch
                                checked={termsRequired}
                                onCheckedChange={checked => {
                                    if (checked) {
                                        setTimeout(() => {
                                            setFocus('terms');
                                        }, 0);
                                    }
                                    setTermsRequired(checked as boolean);
                                }}
                            />
                        </div>
                        {termsRequired && (
                            <>
                                <Form.Textarea
                                    rows={8}
                                    name={'terms'}
                                    control={control}
                                    rules={{
                                        required: termsRequired
                                            ? 'Must provide terms & conditions to enforce their acknowledgement.'
                                            : undefined,
                                    }}
                                />
                                <Form.Error name={path('terms')} />
                            </>
                        )}
                    </div>
                    <div
                        className={'flex items-center justify-between !border-none pb-8 pl-8 pr-8'}
                    >
                        <div>
                            {cancelAuction && (
                                <>
                                    {auction?.can_delete && (
                                        <>
                                            <Button
                                                variant="none"
                                                color="error"
                                                onClick={() => onDelete()}
                                            >
                                                Delete Auction
                                            </Button>
                                            <DeleteAuction
                                                isOpen={showDeleteAuction}
                                                onIsOpenChange={(open: boolean) =>
                                                    setShowDeleteAuction(open)
                                                }
                                                onClickDeleteAuction={() => onDeleteConfirm()}
                                            />
                                        </>
                                    )}
                                    {auction && !auction.can_delete && auction.can_cancel && (
                                        <>
                                            <Button
                                                variant="none"
                                                color="error"
                                                onClick={() => onCancel()}
                                            >
                                                Cancel Auction
                                            </Button>
                                            <CancelAuction
                                                isOpen={showCancelAuction}
                                                onIsOpenChange={(open: boolean) =>
                                                    setShowCancelAuction(open)
                                                }
                                                onClickCancelAuction={() => onCancelConfirm()}
                                            />
                                        </>
                                    )}
                                </>
                            )}
                        </div>
                        <Form.SubmitButton disabled={isFetching}>
                            {auction ? 'Save Edits' : 'Create Auction'}
                        </Form.SubmitButton>
                    </div>
                </form>
            </Box>
            <BidderNumberConfirmation.Root
                open={showConfirmationDialog}
                onConfirm={onSubmitConfirmation}
            />
        </Form.Provider>
    );
};

export { AuctionSettings };
